import React, { useEffect, useRef, useState } from 'react';
import * as Icons from 'lucide-react';
import { stats } from '../data/mock';
import { useLang } from '../context/LanguageContext';

function useCountUp(target, start) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf;
    const duration = 1600;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.floor(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return value;
}

function StatItem({ s, start }) {
  const Icon = Icons[s.icon] || Icons.Activity;
  const v = useCountUp(s.value, start);
  const { t } = useLang();
  return (
    <div className="text-center">
      <div className="w-16 h-16 rounded-full bg-[#f5b300]/12 mx-auto flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-[#c89200]" strokeWidth={1.6} />
      </div>
      <p className="text-[40px] md:text-[46px] font-extrabold text-[#14110f] leading-none">
        {v.toLocaleString()}<span className="text-[#f5b300]">{s.suffix}</span>
      </p>
      <p className="text-[14px] text-neutral-500 mt-2 font-medium">{t(s.labelKey)}</p>
    </div>
  );
}

export default function StatsCounter() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold: 0.3 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={ref} className="py-16 bg-white border-y border-neutral-100">
      <div className="max-w-[1280px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-10">
        {stats.map((s) => <StatItem key={s.id} s={s} start={visible} />)}
      </div>
    </section>
  );
}
